import * as React from 'react';
import { ICardEstimateProps } from './types';
import { CardDateTimeIndicator } from '../../CardDateTimeIndicator/CardDateTimeIndicator';
import { ESTIMATE_TOOLTIP_PREFIX } from '../../../wording';
import {
    CARD_ACTIONS_PORTAL,
    CARD_CLASS,
    CARD_CONTROL_CLASS,
    CARD_TEMPLATES_PORTAL,
    DEFAULT_PORTAL
} from '../../../constants';
import { CLASS_ABSTRACT_DASHBOARD_SCROLL_PREVENT } from '../../../../../../abstracts/constants';
import { TOP_BAR_CLASS } from '../../../../topBar/constants';
import { Button, ButtonDropdown, Input } from 'kui';
import { getAsidePanelTooltip } from '../../../../../aside_panel/asidePanel/helpers/getAsidePanelTooltip';
import {
    CARD_ESTIMATE_EXAMPLE,
    CARD_ESTIMATE_LABEL_TEXT,
    CARD_ESTIMATE_REMOVE_TOOLTIP
} from '../../../../../aside_panel/cardDetails/EstimateAndSpentTimeSection/components/CardEstimateAndSpentTimeSection/constants';
import {
    parseEstimate
} from '../../../../../aside_panel/cardDetails/EstimateAndSpentTimeSection/components/CardEstimateAndSpentTimeSection/behaviour/parseEstimate';
import './_CardEstimate.scss';

export function CardEstimate({
    estimate,
    listId,
    isDisabled,
    isReadonly,
    isTemplates,
    workHoursInDay,
    onChange,
    onOpen,
    onShowSnackbar,
    setOpen: _setOpenOwn,
}: ICardEstimateProps) {
    const className = CARD_CLASS + '__estimate';

    const [isOpen, _setOpen] = React.useState(null);
    const [inputState, setInputState] = React.useState(null);
    const [showValid, setShowValid] = React.useState(null);
    const [estimateStrBtn, setEstimateStrBtn] = React.useState(estimate);
    const [estimateStr, setEstimateStr] = React.useState(estimate);

    const setOpen = (isOpen: boolean) => {
        _setOpen(isOpen);
        _setOpenOwn(isOpen);
    };

    const getSyntheticEventTargetValue = (
        e: React.SyntheticEvent,
    ): string => {
        const target = e.target as HTMLInputElement;
        return target.value || ''
    };

    const isEventTargetValueValid = (
        e: React.SyntheticEvent,
    ): boolean => {
        const targetValue = getSyntheticEventTargetValue(e);
        const isValid = targetValue.trim().length === 0 || parseEstimate(targetValue, workHoursInDay) > 0;
        return isValid;
    };

    const handleChange = (
        e: React.SyntheticEvent,
    ) => {
        const isValid = isEventTargetValueValid(e);
        if (showValid) {
            const inputStateStr = isValid ? 'success' : 'error';
            setInputState(inputStateStr);
        } else {
            setInputState(null);
        }
        if (isValid) {
            const targetValue = getSyntheticEventTargetValue(e);
            const estimateInMinutes = parseEstimate(targetValue, workHoursInDay);
            onChange(estimateInMinutes);
        }
    };

    const onBlur = (
        e: React.FocusEvent,
    ) => {
        const targetValue = getSyntheticEventTargetValue(e);
        const isValid = isEventTargetValueValid(e);
        setShowValid(!isValid);
        if (!isValid) {
            setInputState('error');
        } else {
            setInputState(null);
            const estimateInMinutes = parseEstimate(targetValue, workHoursInDay);
            const prevEstimate = parseEstimate(estimate, workHoursInDay);
            if (prevEstimate !== estimateInMinutes) {
                onChange(estimateInMinutes);
            }
        }
    };

    const onEnter = (
        e: React.KeyboardEvent,
    ) => {
        const targetValue = getSyntheticEventTargetValue(e);
        const isValid = isEventTargetValueValid(e);
        setShowValid(!isValid);
        if (!isValid) {
            setInputState('error');
        } else {
            setInputState(null);
            const estimateInMinutes = parseEstimate(targetValue, workHoursInDay);
            const prevEstimate = parseEstimate(estimate, workHoursInDay);
            if (prevEstimate !== estimateInMinutes) {
                onChange(estimateInMinutes);
            }
            setOpen(false);
        }
    };

    React.useEffect(() => {
        if (isOpen) return;
        setEstimateStrBtn(estimate);
        setInputState(null);
        setShowValid(null);
    }, [estimate, isOpen]);

    React.useEffect(() => {
        function onScroll() {
            setOpen(false);
        }
        if (isOpen && !isTemplates) {
            window.addEventListener('scroll', onScroll);
        }

        return () => window.removeEventListener('scroll', onScroll);
    }, [isOpen]);

    return <div
        className={`${className}-wrapper ${CARD_CONTROL_CLASS}`}
        onClick={() => {
            if (!isDisabled || isReadonly) return;
            onShowSnackbar();
        }}
    >
        <ButtonDropdown
            className={className}
            dropdownClassName={`
                ${className + '-dropdown'}
                ${CARD_CONTROL_CLASS}
                ${CLASS_ABSTRACT_DASHBOARD_SCROLL_PREVENT}
            `}
            portal
            portalId={isTemplates ? CARD_TEMPLATES_PORTAL : DEFAULT_PORTAL}
            opened={isOpen}
            disabled={isReadonly || isDisabled}
            onClose={() => {
                const topBar = document.querySelector(`.${TOP_BAR_CLASS}`) as HTMLElement;
                if (topBar) {
                    topBar.style.zIndex = '120'; // $z-fold * 1.2
                }
                setOpen(false);
            }}
            onOpen={() => {
                const topBar = document.querySelector(`.${TOP_BAR_CLASS}`) as HTMLElement;
                if (topBar) {
                    topBar.style.zIndex = 'unset';
                }
                onOpen();
                setEstimateStr(estimate);
                setOpen(true);
            }}
        >
            <Button
                variant={'icon'}
                className={className + '-button'}
            >
                <CardDateTimeIndicator tooltip={ESTIMATE_TOOLTIP_PREFIX + estimateStrBtn} text={estimateStrBtn}/>
            </Button>
            <div>
                <Input
                    autoFocus
                    className={className + '-input'}
                    isClearable
                    iconTooltip={
                        getAsidePanelTooltip({
                            direction: 'up-left',
                            value: CARD_ESTIMATE_REMOVE_TOOLTIP,
                        })
                    }
                    label={CARD_ESTIMATE_LABEL_TEXT}
                    tooltip={
                        getAsidePanelTooltip({
                            direction: 'up-right',
                            value: CARD_ESTIMATE_EXAMPLE,
                        })
                    }
                    autosize={false}
                    state={inputState}
                    placeholder={CARD_ESTIMATE_EXAMPLE}
                    onChange={handleChange}
                    onEnter={onEnter}
                    onBlur={onBlur}
                    value={estimateStr}
                />
            </div>
        </ButtonDropdown>
    </div>;
}
